import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, ErrorSummary, InPageAlert, Row, SectionPage } from '@snsw/react-component-library';
import { useDispatch, useSelector } from 'react-redux';
import ScrollToTop from 'shared/utils/scrollToTop';
import { storePlantDesignDetails } from 'plantDesign/redux/plantDesignSlice';
import { CommonDesignDetails } from './components/CommonDesignDetails';
import { BoilerDesignDetails } from './components/BoilerDesignDetails';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { MobileCraneDesignDetails } from './components/MobileCraneDesignDetails';
import { TowerCraneDesignDetails } from './components/TowerCraneDesignDetails';
import { EscalatorDesignDetails } from './components/EscalatorDesignDetails';
import { LiftDesignDetails } from './components/LiftDesignDetails';
import { BuildingMaintenanceUnitDesignDetails } from './components/BuildingMaintenanceUnitDesignDetails';
import { PressureVesselDesignDetails } from './components/PressureVesselDesignDetails';
import { GantryCraneDesignDetails } from './components/GantryCraneDesignDetails';
import { BridgeCraneDesignDetails } from './components/BridgeCraneDesignDetails';
import { ConcretePlacingBoomDesignDetails } from './components/ConcretePlacingBoomDesignDetails';
import { GasCylinderDesignDetails } from './components/GasCylinderDesignDetails';
import { HoistWithPlatformMovementDesignDetails } from './components/HoistWithPlatformMovementDesignDetails';
import { SuspendedWorkboxDesignDetails } from './components/SuspendedWorkboxDesignDetails';
import { AmusementDeviceDesignDetails } from './components/AmusementDeviceDeignDetails';
import { VehicleHoistDesignDetails } from './components/VehicleHoistDesignDetails';
import { PrefabricatedScaffoldingDesignDetails } from './components/PrefabricatedScaffoldingDesignDetails';
import { PassengerRopewayDesignDetails } from './components/PassengerRopewayDesignDetails';
import { BoomTypeElevatingWorkPlatformDesignDetails } from './components/BoomTypeElevatingWorkPlatformDesignDetails';
import { MastClimbingWorkPlatformDesignDetails } from './components/MastClimbingWorkPlatformDesignDetails';
import { PlantTypeDesignDetails } from './components/PlantTypeDesignDetails';
import { PagePath } from 'shared/constants/pagePath';
import { PasswordlessContinueSaveExitButtons } from 'shared/components/formNavigationButtons/PasswordlessContinueSaveExitButtons';
import { saveApplication } from 'shared/redux/applicationSlice';
import { PlantDesignConstants } from 'plantDesign/constants/plantDesignConstants';

export const PlantDesignDetails = () => {
  ScrollToTop();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const refData = useSelector((state) => state.plantDesignReferenceData.data);
  const storedDetails = useSelector((state) => state.plantDesign);

  const [plantType, setPlantType] = useState(storedDetails?.plantType);
  const [subType, setSubType] = useState(storedDetails?.subType);
  const [registrationType, setRegistrationType] = useState(storedDetails?.registrationType);
  const [plantTypeDetails, setPlantTypeDetails] = useState();
  const [commonDetails, setCommonDetails] = useState();
  const [plantSpecificDetails, setPlantSpecificDetails] = useState();
  const [isPlantTypeDetailsCompleted, setIsPlantTypeDetailsCompleted] = useState(false);

  const [plantTypeErrorSummary, setPlantTypeErrorSummary] = useState([]);
  const [commonErrorSummary, setCommonErrorSummary] = useState([]);
  const [plantSpecificErrorSummary, setPlantSpecificErrorSummary] = useState([]);
  const [combinedErrorSummary, setCombinedErrorSummary] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  const [canContinue, setCanContinue] = useState(true);
  const [isCorrection, setIsCorrection] = useState(false);
  const [continueClicked, setContinueClicked] = useState(false);

  useEffect(() => {
    setPlantType(plantTypeDetails?.plantType);
    setSubType(plantTypeDetails?.subType);
    setRegistrationType(plantTypeDetails?.registrationType);

    setIsPlantTypeDetailsCompleted(plantTypeDetailsRef?.current?.validate());
  }, [plantTypeDetails]);

  useEffect(() => {
    setShowErrors(false);
    setContinueClicked(false);
  }, [plantType, registrationType]);

  useEffect(() => {
    setCombinedErrorSummary([...plantTypeErrorSummary, ...commonErrorSummary, ...plantSpecificErrorSummary]);
  }, [plantTypeErrorSummary, commonErrorSummary, plantSpecificErrorSummary]);

  const plantTypeDetailsRef = React.useRef();
  const commmonDetailsRef = React.useRef();
  const plantSpecificDetailsRef = React.useRef();

  const validateAll = () => {
    const plantTypeDetailsValid = plantTypeDetailsRef.current ? plantTypeDetailsRef.current.validate() : true;
    const commonDetailsValid = commmonDetailsRef.current ? commmonDetailsRef.current.validate() : true;
    const specificDetailsValid = plantSpecificDetailsRef.current ? plantSpecificDetailsRef.current.validate() : true;

    return plantTypeDetailsValid && commonDetailsValid && specificDetailsValid;
  };

  const storeDetails = (isCompleted) => {
    let details = {
      isCompleted,
      ...plantTypeDetails,
      ...commonDetails,
      plantSpecificDetails,
    };

    dispatch(storePlantDesignDetails(details));
  };

  const onContinueClick = () => {
    setContinueClicked(true);
    if (!canContinue) return;

    if (validateAll()) {
      setShowErrors(false);
      storeDetails(true);

      dispatch(saveApplication(true))
        .unwrap()
        .then(() => {
          navigate(PagePath.plantDesignDrawingAndTechnicalDetails);
        })
        .catch(() => {});
    } else {
      setShowErrors(true);
    }
  };

  const onPreSaving = () => {
    const isCompleted = validateAll();
    storeDetails(isCompleted);
  };

  const onBackClick = () => {
    storeDetails(validateAll());
    navigate(PagePath.plantDesignRegistrationHolder);
  };

  const plantSpecificComponents = {
    [PlantDesignRefData.plantRefs.boiler.code]: BoilerDesignDetails,
    [PlantDesignRefData.plantRefs.pressureVessel.code]: PressureVesselDesignDetails,
    [PlantDesignRefData.plantRefs.gasCylinder.code]: GasCylinderDesignDetails,
    [PlantDesignRefData.plantRefs.lift.code]: LiftDesignDetails,
    [PlantDesignRefData.plantRefs.escalator.code]: EscalatorDesignDetails,
    [PlantDesignRefData.plantRefs.movingWalkway.code]: EscalatorDesignDetails, // Moving Walkway has the same fields as Escalator.
    [PlantDesignRefData.plantRefs.hoistWithPlatformMovement.code]: HoistWithPlatformMovementDesignDetails,
    [PlantDesignRefData.plantRefs.vehicleHoist.code]: VehicleHoistDesignDetails,
    [PlantDesignRefData.plantRefs.buildingMaintenanceUnit.code]: BuildingMaintenanceUnitDesignDetails,
    [PlantDesignRefData.plantRefs.towerCrane.code]: TowerCraneDesignDetails,
    [PlantDesignRefData.plantRefs.mobileCrane.code]: MobileCraneDesignDetails,
    [PlantDesignRefData.plantRefs.bridgeCrane.code]: BridgeCraneDesignDetails,
    [PlantDesignRefData.plantRefs.gantryCrane.code]: GantryCraneDesignDetails,
    [PlantDesignRefData.plantRefs.suspendedWorkbox.code]: SuspendedWorkboxDesignDetails,
    [PlantDesignRefData.plantRefs.prefabricatedScaffolding.code]: PrefabricatedScaffoldingDesignDetails,
    [PlantDesignRefData.plantRefs.concretePlacingBoom.code]: ConcretePlacingBoomDesignDetails,
    [PlantDesignRefData.plantRefs.amusementDevice.code]: AmusementDeviceDesignDetails,
    [PlantDesignRefData.plantRefs.passengerRopeway.code]: PassengerRopewayDesignDetails,
    [PlantDesignRefData.plantRefs.boomTypeElevatingWorkPlatform.code]: BoomTypeElevatingWorkPlatformDesignDetails,
    [PlantDesignRefData.plantRefs.mastClimbingWorkPlatform.code]: MastClimbingWorkPlatformDesignDetails,
  };

  const PlantSpecificDesignDetails = plantSpecificComponents[plantType];

  return (
    <>
      {showErrors && !!combinedErrorSummary?.length && <ErrorSummary errors={combinedErrorSummary} />}

      {isCorrection && continueClicked && (
        <InPageAlert variant="error" title="You cannot make a correction">
          <p>You can only register a new plant design or alteration. Please check your selection for:</p>
          <ul>
            <li>Type of registration</li>
          </ul>
        </InPageAlert>
      )}

      {refData && (
        <SectionPage>
          <Row>
            <Col span={8}>
              <PlantTypeDesignDetails
                refData={refData}
                onDetailsChange={setPlantTypeDetails}
                onErrorChange={setPlantTypeErrorSummary}
                showErrors={showErrors}
                setCanContinue={setCanContinue}
                setIsCorrection={setIsCorrection}
                ref={plantTypeDetailsRef}
              />

              {isPlantTypeDetailsCompleted && canContinue && !!PlantSpecificDesignDetails && (
                <>
                  <CommonDesignDetails
                    plantType={plantType}
                    registrationType={registrationType}
                    onDetailsChange={setCommonDetails}
                    onErrorChange={setCommonErrorSummary}
                    setCanContinue={setCanContinue}
                    showErrors={showErrors}
                    ref={commmonDetailsRef}
                  />
                  <PlantSpecificDesignDetails
                    refData={refData}
                    value={storedDetails?.plantSpecificDetails}
                    subType={subType}
                    onChange={setPlantSpecificDetails}
                    onErrorChange={setPlantSpecificErrorSummary}
                    ref={plantSpecificDetailsRef}
                  />
                </>
              )}
            </Col>
          </Row>
        </SectionPage>
      )}

      <PasswordlessContinueSaveExitButtons
        onBackClick={onBackClick}
        onContinueClick={onContinueClick}
        onPreSaving={onPreSaving}
        licenceType={PlantDesignConstants.licenceType}
      />
    </>
  );
};
